'use client';

import * as React from 'react';
import {Menu, Bell, User} from 'lucide-react';
import {Button} from '@purpose/ui/components/button/button';
import useTheme from '@purpose/ui/hooks/useTheme';
import {useResponsive} from '@purpose/ui/hooks/useResponsive';
import {UserHeaderInfo} from '@purpose/ui/components/user-header-info/user-header-info';
import {ExpandableSearch} from '@purpose/ui/components/expandable-search/expandable-search';
import {cn} from '@purpose/ui/lib/utils';

export interface HeaderProps {
  userName?: string | null;
  avatarUrl?: string | null;
  onMenuClick?: () => void;
  onSettingsClick?: () => void;
  onNotificationsClick?: () => void;
  onAvatarClick?: () => void;
  color?: string | null;
  languagePicker?: React.ReactNode;
  newNotificationsExist?: boolean;
  renderBlockBeforeSearch?: () => React.ReactNode;
  searchProps: {
    results: {id: number; name: string; title?: string}[];
    inputValue: string;
    setInputValue: (inp: string) => void;
    setSearchValue: (inp: string) => void;
    onClose: () => void;
    placeholder?: string;
  };
}

const Header = ({
  userName,
  avatarUrl,
  onMenuClick,
  searchProps,
  onSettingsClick,
  onNotificationsClick,
  onAvatarClick,
  newNotificationsExist,
  color,
  languagePicker,
  renderBlockBeforeSearch
}: HeaderProps) => {
  const {colors} = useTheme();
  const {isMobile, isTabletOrSmaller} = useResponsive();
  const [mounted, setMounted] = React.useState(false);
  const [isSearchFieldVisible, setIsSearchFieldVisible] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  const handleSearchFieldVisibilityChange = (isVisible: boolean) => {
    setIsSearchFieldVisible(isVisible);
  };

  const backgroundColor = color || colors.primary.base;

  return (
    <header
      className={cn(
        'fixed top-0 right-0 left-0 z-50 flex h-20 items-center justify-center shadow-none',
        'px-3 py-2 sm:px-4 md:px-5 lg:px-8'
      )}
      style={{
        backgroundColor: mounted ? backgroundColor : undefined,
        opacity: mounted ? 1 : 0
      }}
    >
      <div className="flex w-full items-center justify-between px-1 sm:px-1">
        {/* Left side - Menu and User Info */}

        <div className="flex flex-row items-center gap-2 md:gap-5 lg:gap-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={onMenuClick}
            className="p-2 text-white hover:bg-black/20! hover:text-white"
            aria-label="Meny"
          >
            <Menu className="h-6 w-6" />
          </Button>

          {(!isSearchFieldVisible || !isTabletOrSmaller) && (
            <UserHeaderInfo
              onClick={onAvatarClick}
              avatarUrl={avatarUrl}
              userName={userName}
            />
          )}
        </div>

        {/* Right side - Search and Actions */}
        <div
          className={cn(
            'flex items-center justify-end gap-1',
            isSearchFieldVisible && isTabletOrSmaller ? 'w-full' : 'w-auto'
          )}
        >
          {!(isTabletOrSmaller && isSearchFieldVisible) &&
            renderBlockBeforeSearch?.()}
          {searchProps && (
            <ExpandableSearch
              width={isTabletOrSmaller ? '100%' : 371}
              {...searchProps}
              placeholder={searchProps.placeholder}
              onSearchFieldVisibilityChange={handleSearchFieldVisibilityChange}
              buttonClassName="hover:bg-black/20! px-3 text-secondary-30"
              iconClassName="h-5 w-5"
            />
          )}
          {!isSearchFieldVisible && !isTabletOrSmaller && !isMobile && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onNotificationsClick}
              className="text-secondary-30 hover:text-secondary-30! hover:bg-black/20!"
              aria-label="Innstillinger"
            >
              <div className="relative">
                <Bell className="h-5 w-5" />
                {newNotificationsExist && (
                  <div className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-yellow-500" />
                )}
              </div>
            </Button>
          )}
          {!isMobile && (!isSearchFieldVisible || !isTabletOrSmaller) && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={onSettingsClick}
                className="text-secondary-30 hover:text-secondary-30! hover:bg-black/20!"
                aria-label="Varsler"
              >
                <User className="h-5 w-5" />
              </Button>
              {languagePicker}
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export {Header};
